import { Sprite, SpriteAtlas, DrawContext } from '@cs/engine';
import { World } from './world';
import { Road } from './road';
import { Building, BuildingType, BuildingDescription, building_description, cost_for_building } from './building';
import { AtlasCoord, JsonDictionaryWrapper } from './utils';
import { Enemy } from './enemy';
import { Shot } from './gun';
import { WorldPoint, Direction, opposite } from './grid';
import { Resource, resource_image } from './resources';
import { UI } from './ui';
import { Command, CommandViability, Packet } from './command';
import { ScreenPoint } from '@cs/engine';

export class ViewportState {
    public ox: number = 0
    public oy: number = 0
    public scale: number = 1

    public pannable_screen_to_virtual_screen(pt: ScreenPoint): ScreenPoint {
        return new ScreenPoint((pt.x - this.ox) / this.scale, (pt.y - this.oy) / this.scale)
    }

    public transform_ctx(ctx: DrawContext): void {
        ctx.translate(this.ox, this.oy)
        ctx.scale(this.scale, this.scale)
    }
}

// this is the viewport in terms of pannable coordinates
export class ViewportTransformation {
    static min_scale: number = 0.5
    static max_scale: number = 3.0

    private firm_vs: ViewportState
    private provisional_vs: ViewportState | null

    constructor() {
        this.firm_vs = new ViewportState()
        this.provisional_vs = null
    }

    public snap_to(i: number, j: number): void {
        this.firm_vs.ox = i
        this.firm_vs.oy = j,
        this.firm_vs.scale =  this.firm_vs.scale 
        this.provisional_vs = null
    }

    public get state(): ViewportState {
        if (this.provisional_vs != null) {
            return this.provisional_vs
        }
            
        return this.firm_vs
    }

    // the passed screen point is the pannable point this is around
    public zoom(dz: number, around: ScreenPoint): void {
        const before = this.state.pannable_screen_to_virtual_screen(around)
        this.firm_vs.scale += dz

        if (this.firm_vs.scale < ViewportTransformation.min_scale) {
            this.firm_vs.scale = ViewportTransformation.min_scale
        } else if (this.firm_vs.scale > ViewportTransformation.max_scale) {
            this.firm_vs.scale = ViewportTransformation.max_scale
        }

        const after = this.state.pannable_screen_to_virtual_screen(around)
        const dx = before.x - after.x
        const dy = before.y - after.y
        this.firm_vs.ox -= dx * this.firm_vs.scale
        this.firm_vs.oy -= dy * this.firm_vs.scale
        const final = this.state.pannable_screen_to_virtual_screen(around)
    }

    public drag(dx: number, dy: number): void {
        if (this.provisional_vs == null) {
            this.provisional_vs = new ViewportState()
        }

        this.provisional_vs.ox = this.firm_vs.ox + dx
        this.provisional_vs.oy = this.firm_vs.oy + dy
        this.provisional_vs.scale = this.firm_vs.scale
    }

    public finish_drag(): void {
        if (this.provisional_vs != null) {
            this.firm_vs = this.provisional_vs
            this.provisional_vs = null
        }
    }
}

// this is the viewport in terms of the offset grid
export class Viewport {
    // this requires an integral world point
    public world_vertex_to_screen(sp: WorldPoint): ScreenPoint {
        let pt = new ScreenPoint(sp.x, sp.y * 0.25)

        // stagger the rows
        if (sp.staggered) {
            pt.x += 0.5
        }

        /*
          indent for screen alignment
          y is unnecessary as the target is the bottom half
         */
        pt.x += 0.5

        return pt
    }

    public world_lerped_to_screen(source: WorldPoint, destination: WorldPoint, destination_prop: number): ScreenPoint {
        const pt_from = this.world_vertex_to_screen(source)
        const pt_to = this.world_vertex_to_screen(destination)
        return new ScreenPoint(
            pt_to.x * destination_prop + (1.0 - destination_prop) * pt_from.x,
            pt_to.y * destination_prop + (1.0 - destination_prop) * pt_from.y
        )
    }

    public screen_to_world_vertex(sp: ScreenPoint): WorldPoint {
        const rough_x: number = Math.round(sp.x)
        const rough_y: number = Math.round(sp.y * 4)

        let wp = new WorldPoint(0, 0)
        let distance: number | null = null

        for (let di = -2; di <= 2; di += 1) {
            for (let dj = -3; dj <= 3; dj += 1) {
                const n_wp = new WorldPoint(di + rough_x, dj + rough_y)
                const test_point = this.world_vertex_to_screen(n_wp) 
                test_point.y += 0.25
                const n_distance = sp.distance_to(test_point)
                if (distance == null || n_distance < distance) {
                    distance = n_distance
                    wp = n_wp
                }
            }
        }

        return wp
    }
}

export enum RoadState {
    Provisional,
    Firm,
    Selected,
    Unaffordable,
}

export class Renderer {
    private atlas: SpriteAtlas
    private viewport: Viewport

    constructor(atlas: SpriteAtlas, vp: Viewport) {
        this.atlas = atlas
        this.viewport = vp
    }

    private sprite_for_direction(d: Direction): Sprite {
        switch (d) {
            case Direction.N:
                return this.atlas.create_sprite(2, 3, 1, 1)
                
            case Direction.E:
                return this.atlas.create_sprite(0, 3, 1, 1)

            case Direction.S:
                return this.atlas.create_sprite(3, 3, 1, 1)

            case Direction.W:
                return this.atlas.create_sprite(1, 3, 1, 1)

            case Direction.NE:
                return this.atlas.create_sprite(5, 3, 1, 1)

            case Direction.SE:
                return this.atlas.create_sprite(6, 3, 1, 1)

            case Direction.SW:
                return this.atlas.create_sprite(7, 3, 1, 1)

            case Direction.NW:
                return this.atlas.create_sprite(4, 3, 1, 1)
        }
    }

    private cross_sprite_for_point(i: number, j: number): Sprite {
        while (i < 0)  {
            i += 4
        }
        while (j < 0)  {
            j += 2
        }
        return this.atlas.create_sprite(10 + (i % 4), j % 2, 1, 1)
    }

    private draw_spawn(ctx: DrawContext, world: World): void {
        const spawn_marker:Sprite = this.atlas.create_sprite(1, 1, 1, 1)
        const pt = this.viewport.world_vertex_to_screen(world.map.enemy_spawn)
        ctx.save()
        ctx.translate(pt.x, pt.y)
        ctx.draw_sprite(spawn_marker)
        ctx.restore()
    }

    private draw_grid(ctx: DrawContext, world: World, tl: WorldPoint, br: WorldPoint): void {
        const occupied_sprite = this.atlas.create_sprite(5, 1, 1, 1)
        for (var i = tl.x; i <= br.x; i += 1) {
            for (var j = tl.y; j < br.y; j += 1) {
                const wp = new WorldPoint(i, j)
                const pt = this.viewport.world_vertex_to_screen(wp)
                if (world.is_occupied(wp)) {
                    ctx.save()
                    ctx.translate(pt.x, pt.y)
                    ctx.draw_sprite(occupied_sprite)
                    ctx.restore()
                }
            }
        }

        for (var i = tl.x; i <= br.x; i += 1) {
            for (var j = tl.y; j < br.y; j += 1) {
                const pt = this.viewport.world_vertex_to_screen(new WorldPoint(i, j))
                ctx.save()
                ctx.translate(pt.x, pt.y)
                ctx.draw_sprite(this.cross_sprite_for_point(i, j))
                ctx.restore()
            }
        }
    }

    private draw_enemy(ctx: DrawContext, e: Enemy): void {
        const evs = e.visual_state

        const pt_lerped = this.viewport.world_lerped_to_screen(evs.source, evs.destination, evs.proportion);

        ctx.save()
        ctx.translate(pt_lerped.x, pt_lerped.y)
        ctx.draw_sprite(this.lookup_atlas_coord(e.sprite()))
        ctx.restore()

        this.draw_hp(ctx, e.hp_proportion, pt_lerped)
    }

    private draw_road(ctx: DrawContext, r: Road, state: RoadState): void {
        let x:number
        let y:number
        switch (state) {
        case RoadState.Firm:
            x = 0
            y = 3
            break

        case RoadState.Provisional:
            x = 0
            y = 4
            break

        case RoadState.Unaffordable:
            x = 14
            y = 1
            break

        case RoadState.Selected:
            x = 14
            y = 2
            break
        }

        let wp = r.start.copy()
        for (let d of r.route.directions) {
            ctx.save()
            const pt_before = this.viewport.world_vertex_to_screen(wp)
            ctx.translate(pt_before.x, pt_before.y)

            switch (d) {
                case Direction.E:
                    ctx.translate(0.5, 0.0)
                    ctx.draw_sprite(this.atlas.create_sprite(x, y, 1, 1))
                    break;

                case Direction.W:
                    ctx.translate(-0.5, 0.0)
                    ctx.draw_sprite(this.atlas.create_sprite(x, y, 1, 1))
                    break;

                case Direction.S:
                    ctx.translate(0, 0.25)
                    ctx.draw_sprite(this.atlas.create_sprite(x + 1, y, 1, 1))
                    break;

                case Direction.N:
                    ctx.translate(0, -0.25)
                    ctx.draw_sprite(this.atlas.create_sprite(x + 1, y, 1, 1))
                    break;

                case Direction.SE:
                    ctx.translate(0.25, 0.125)
                    ctx.draw_sprite(this.atlas.create_sprite(x + 2, y, 1, 1))
                    break;

                case Direction.NW:
                    ctx.translate(-0.25, -0.125)
                    ctx.draw_sprite(this.atlas.create_sprite(x + 2, y, 1, 1))
                    break;

                case Direction.SW:
                    ctx.translate(-0.25, 0.125)
                    ctx.draw_sprite(this.atlas.create_sprite(x + 3, y, 1, 1))
                    break;

                case Direction.NE:
                    ctx.translate(0.25, -0.125)
                    ctx.draw_sprite(this.atlas.create_sprite(x + 3, y, 1, 1))
                    break;
            }


            ctx.restore()
            wp.translate(d)
        }

        if (state != RoadState.Provisional) {
            const cs = r.visual_state()
            const carrier_sprite:Sprite = this.atlas.create_sprite(0, 2, 1, 1)
            const pt_lerped = this.viewport.world_lerped_to_screen(cs.source, cs.destination, cs.proportion);

            ctx.save()
            ctx.translate(pt_lerped.x, pt_lerped.y)
            ctx.draw_sprite(carrier_sprite)
            if (cs.carried != null) {
                ctx.draw_sprite(this.lookup_atlas_coord(resource_image(cs.carried.resource)))
            }
            ctx.restore()
        }
    } 

    private draw_shot(ctx: DrawContext, s: Shot): void {
        const shot_sprite:Sprite = this.atlas.create_sprite(1, 1, 1, 1)
        const proportion:number = s.t / s.transit_ticks
        const pt_lerped = this.viewport.world_lerped_to_screen(s.source, s.destination, proportion);

        ctx.save()
        ctx.translate(pt_lerped.x, pt_lerped.y)
        ctx.draw_sprite(shot_sprite)
        ctx.restore()
    }

    private draw_building(ctx: DrawContext, b: Building, provisional: boolean): void {
        const pt = this.viewport.world_vertex_to_screen(b.position)
        ctx.save()
        if (provisional) {
            ctx.alpha(0.5)
        }
        const im = b.current_image

        ctx.translate(pt.x, pt.y - 0.5 * (im.height - 1))
        let sprite = this.atlas.create_sprite(im.root.i, im.root.j, im.dimension, im.height)
        ctx.draw_sprite(sprite)
        ctx.restore()

        const title = b.description.placeholder_title
        if (title != null) {
            ctx.save()
            ctx.translate(pt.x - 0.5 * im.dimension + 0.5, pt.y - 0.3)
            ctx.canvas_context().font = "italic 0.3px serif";
            ctx.canvas_context().fillStyle = "#000000"
            ctx.canvas_context().fillText(title, 0, 0)
            ctx.restore()
        }


        // parcels
        let rp = b.position.copy()
        for (let parcel of b.waiting_parcels) {
            const pt = this.viewport.world_vertex_to_screen(rp)
            ctx.save()
            ctx.translate(pt.x, pt.y)
            ctx.draw_sprite(this.lookup_atlas_coord(resource_image(parcel.resource)))
            ctx.restore()
            rp.x += 0.25
        }

        if (!provisional) {
            this.draw_hp(ctx, b.hp_proportion, pt)
        }
    }

    private lookup_atlas_coord(ac: AtlasCoord): Sprite {
        return this.atlas.create_sprite(ac.i, ac.j, 1, 1)
    }

    private draw_hp(ctx: DrawContext, prop: number, pt: ScreenPoint) {
        const hp_bg:Sprite = this.atlas.create_sprite(7, 2, 1, 1)
        const hp_fg:Sprite = this.atlas.create_sprite(8, 2, 1, 1)

        ctx.save()
        ctx.translate(pt.x, pt.y)
        ctx.draw_sprite(hp_bg)
        ctx.translate(prop - 0.5, 0)
        ctx.draw_sprite(hp_fg)
        ctx.restore()
    }

    public draw_world(ctx: DrawContext, world: World, ui: UI, tl: WorldPoint, br: WorldPoint) {
        const tile_highlight_sprite:Sprite = this.atlas.create_sprite(0, 1, 1, 1)
        const denied_sprite:Sprite = this.atlas.create_sprite(1, 1, 1, 1)
        const no_resource_sprite:Sprite = this.atlas.create_sprite(4, 1, 1, 1)
        const unaffordable_sprite:Sprite = this.atlas.create_sprite(2, 1, 1, 1)
        const selected_sprite:Sprite = this.atlas.create_sprite(3, 1, 1, 1)

        // draw grid
//        this.draw_grid(ctx, world, tl, br)

        // bg ornaments
        for (let orn of world.map.ornaments) {
            const pt = this.viewport.world_vertex_to_screen(orn.world_root)
            ctx.save()
            // the 0.5 might be wrong, potentially I've aligned just for odd sized
            ctx.translate(pt.x + 0.5, pt.y + 0.5)
            ctx.draw_sprite(this.atlas.create_sprite(orn.sprite_root.i, orn.sprite_root.j, orn.width, orn.height));
            ctx.restore()
        }

        // spawn 
        this.draw_spawn(ctx, world)

        //
        for (let dep of world.map.deposits) {
            const pt = this.viewport.world_vertex_to_screen(dep.position)
            const im = dep.image()
            ctx.save()
            ctx.translate(pt.x, pt.y)
            ctx.draw_sprite(this.atlas.create_sprite(im.i, im.j, 1, 1));
            ctx.restore()
        }

        // selection
        const currently_selected = ui.selection
        if (currently_selected != null) {
            switch (currently_selected.kind) {
                case 'Building':
                    for (let ot of currently_selected.building.occupied_tiles()) {
                        const pt = this.viewport.world_vertex_to_screen(ot)
                        ctx.save()
                        ctx.translate(pt.x, pt.y)
                        ctx.draw_sprite(selected_sprite);
                        ctx.restore()
                    }
                    break
            }
        }

        // hover cursor
        if (ui.hovered_point != null && ui.pending_command == null) {
            const pt = this.viewport.world_vertex_to_screen(ui.hovered_point)
            ctx.save()
            ctx.translate(pt.x, pt.y)
            ctx.draw_sprite(tile_highlight_sprite);
            if (ui.road_tool) {
                ctx.draw_sprite(denied_sprite);
            }
            ctx.restore()
        }

        for (let r of world.roads) {
            const sel = ui.selection
            if (sel != null && sel.kind == 'Road' && r == sel.road) {
                this.draw_road(ctx, r, RoadState.Selected);
            } else {
                this.draw_road(ctx, r, RoadState.Firm);
            } 
        }

        for (let b of world.buildings) {
            this.draw_building(ctx, b, false)
        }

        for (let e of world.enemies) {
            this.draw_enemy(ctx, e)
        }

        for (let s of world.shots) {
            this.draw_shot(ctx, s)
        }

        const cmd = ui.pending_command
        if (cmd != null) {
            switch (cmd.cmd.kind) {
                case 'CreateBuilding': {
                    this.draw_building(ctx, world.create_building(cmd.cmd), true)

                    const hp = ui.hovered_point
                    if (hp != null) {
                        const pt = this.viewport.world_vertex_to_screen(hp)
                        ctx.save()
                        ctx.translate(pt.x, pt.y)

                        switch (cmd.viability) {
                            case CommandViability.Ok:
                                break

                            case CommandViability.Unaffordable:
                                ctx.draw_sprite(unaffordable_sprite);
                                break

                            case CommandViability.Conflict:
                                ctx.draw_sprite(denied_sprite);
                                break

                            case CommandViability.TooLong:
                                throw new Error("CommandViability.TooLong makes no sense for a building")
                                break

                            case CommandViability.NoResource:
                                ctx.draw_sprite(no_resource_sprite);
                                break
                        }

                        ctx.restore()
                    }
                    break
                }

                case 'CreateRoad': {
                    if (cmd.viability == CommandViability.Unaffordable) {
                        this.draw_road(ctx, cmd.cmd.road, RoadState.Unaffordable)
                    } else if (cmd.viability == CommandViability.TooLong) {
                        this.draw_road(ctx, cmd.cmd.road, RoadState.Unaffordable)
                    } else {
                        this.draw_road(ctx, cmd.cmd.road, RoadState.Provisional)
                    }
                    break
                }
            }
        }
    }
}

